import { Component } from '@angular/core';
import { Router, RouterLink, RouterModule, RouterOutlet, NavigationEnd } from '@angular/router';
import { ConstantsService } from '../../constants/constants.service';
import { HttpService } from '../../services/http.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { VisitUpdateService } from '../../services/VisitUpdateService';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { QrScannerComponent } from '../qr-scanner/qr-scanner.component';
import { NotificationWebsocketService } from '../../services/notification-websocket.service';
import { ProfileUpdateService } from '../../services/ProfileUpdateService';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterModule, RouterLink, FormsModule, CommonModule, QrScannerComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  host: { id: 'sidebar' },
})
export class SidebarComponent {
  VisitStatusCounts: any[] = [];
  AllVisitorStatusCounts: any = {};
  private visitUpdateSubscription: Subscription = new Subscription();
  private routerSubscription: Subscription = new Subscription();
  roleName: string = '';
  user: any;
  openScanner: boolean = false;
  currentDate: Date = new Date();
  private visitUpdateSub: Subscription | null = null;
  isManager: boolean = false;
  pendingManagerCount: number = 0;

  constructor(
    public constants: ConstantsService,
    private router: Router,
    private httpService: HttpService,
    private toastr: ToastrService,
    private visitUpdateService: VisitUpdateService,
    private wsService: NotificationWebsocketService,
    private profileUpdateService: ProfileUpdateService
  ) {}

  ngOnInit(): void {
    this.preventScrollChaining();
    
    setInterval(() => {
      this.currentDate = new Date();
    }, 1000);

    const storage = this.constants.getSession();
    if (storage) {
      this.roleName = storage.role.name;
    }
    this.getVisitStatusCounts();
    this.getALLVisitorCounts();
    this.isUserManager();
    this.getPendingManagerApprovalCount();

    this.wsService.connect(storage.email);
    
    // Subscribe to updates
    this.visitUpdateSub = this.wsService
      .getVisitUpdates()
      .subscribe((update) => {
        console.log('Received visit update:', update);
        this.getVisitStatusCounts();
        this.getALLVisitorCounts();
        this.getPendingManagerApprovalCount();
      });

    // Subscribe to visit update events
    this.visitUpdateSubscription =
      this.visitUpdateService.visitUpdated$.subscribe(() => {
        this.getVisitStatusCounts();
        this.getALLVisitorCounts();
        this.getPendingManagerApprovalCount();
      });

    this.visitUpdateService.sidebarCountUpdated$.subscribe(() => {
      this.getVisitStatusCounts();
      this.getALLVisitorCounts();
      this.getPendingManagerApprovalCount();
    });

    this.user = this.constants.getSession();
    this.roleName = this.user.role.name;
    
    this.profileUpdateService.profileUpdated$.subscribe(() => {
      const updatedUser = this.constants.getSession();
      this.roleName = updatedUser?.role?.name || '';
      this.user = updatedUser;
    });

    // Subscribe to router events to scroll to active item
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        setTimeout(() => {
          this.scrollToActiveItem();
        }, 100);
      });

    // Initial scroll on component load
    setTimeout(() => {
      this.scrollToActiveItem();
    }, 200);
  }

  /**
   * Prevent scroll chaining: stops scroll events from bubbling to parent elements
   * This prevents the main content from scrolling when the sidebar menu reaches its end
   */
  private preventScrollChaining(): void {
    setTimeout(() => {
      const scrollContainer = document.querySelector('.scroll-sidebar');
      if (scrollContainer) {
        scrollContainer.addEventListener('wheel', (e: any) => {
          const element = e.currentTarget as HTMLElement;
          const isAtTop = element.scrollTop === 0;
          const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 1;

          // If scrolling up at the top or down at the bottom, prevent default
          if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
            e.preventDefault();
          }
        }, { passive: false });

        // Handle touchmove events for mobile
        scrollContainer.addEventListener('touchmove', (e: any) => {
          const element = e.currentTarget as HTMLElement;
          const isAtTop = element.scrollTop === 0;
          const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 1;

          // Allow natural scrolling but prevent bounce
          if ((isAtTop && e.touches[0].clientY > 0) || (isAtBottom && e.touches[0].clientY < 0)) {
            // Don't prevent, just let it scroll naturally
          }
        }, { passive: true });
      }
    }, 0);
  }
  // *********************************************************************************************************************** //

  ngOnDestroy(): void {
    this.visitUpdateSubscription?.unsubscribe();
    this.visitUpdateSub?.unsubscribe();
    this.wsService.disconnect();
  }

  /**
   * Scrolls the sidebar to show the active navigation item
   */
  scrollToActiveItem(): void {
    const sidebarNav = document.querySelector('.sidebar-nav.scroll-sidebar');
    const activeLink = document.querySelector('.sidebar-link.active');

    if (sidebarNav && activeLink) {
      const sidebarRect = sidebarNav.getBoundingClientRect();
      const activeLinkRect = activeLink.getBoundingClientRect();
      
      // Calculate the position to scroll to (center the active item if possible)
      const scrollTop = activeLink.parentElement!.offsetTop - (sidebarRect.height / 2) + (activeLinkRect.height / 2);
      
      sidebarNav.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
    }
  }
  getVisitStatusCounts(): void {
    this.httpService
      .getRequest(this.constants.SERVER_URLS['VISITS_STATUS_COUNT'])
      .subscribe((res: any) => {
        if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
          this.VisitStatusCounts = res.result;
        } else {
          this.toastr.error(res.message );
        }
      });
  }
  getALLVisitorCounts(): void {
    this.httpService
      .getRequest(this.constants.SERVER_URLS['ALL_VISITORS_COUNT'])
      .subscribe((res: any) => {
        if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
          this.AllVisitorStatusCounts = res.result;
        } else {
          this.toastr.error(res.message );
        }

      });
  }

  isUserManager(): void {
  this.httpService
    .getRequest(this.constants.SERVER_URLS['IS_USER_MANAGER'])
    .subscribe((res: any) => {
      if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
        this.isManager = res.result === true;
      } else {
        this.isManager = false;
        this.toastr.error(res.message );
      }
    }, () => {
      this.isManager = false;
    });
}

getPendingManagerApprovalCount(): void {
  this.httpService
    .getRequest(this.constants.SERVER_URLS['GET_PENDING_MANAGER_APPROVAL_COUNT'])
    .subscribe(
      (res: any) => {
        if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
          this.pendingManagerCount = res.result || 0;
        } else {
          this.pendingManagerCount = 0;
          this.isManager = false;
          this.toastr.error(res.message );
        }
      },
      () => {
        this.pendingManagerCount = 0;
        this.isManager = false;
      }
    );
}


 getCount(status: string): number {
  const item = this.VisitStatusCounts.find((s: any) => s.status === status);
  return item ? item.count : 0;
}


  logout() {
    this.wsService.disconnect();
    this.constants.clearSession();
    this.router.navigate(['/login']);
    this.closeSidebar();
  }
  closeSidebar() {
    const wrapper = document.getElementById('main-wrapper');
    if (wrapper) {
      wrapper.classList.remove('show-sidebar');
    }
  }
  viewVisitsByStatus(status: string) {
    this.closeSidebar();
    this.router.navigate(['visit-list'], {
      queryParams: {
        status: status,
      },
    });
  }
   viewManageVisits(status: string) {
    this.closeSidebar();
    this.router.navigate(['visit-list'], {
      queryParams: {
        status: status,
      },
    });
  }

   handleScan(event: any) {
  const result = event?.detail?.result;
  if (result) {
    console.log('Scanned from header:', result);
  }
  this.openScanner = false;
}
handleLogoClick(event: Event): void {
  event.preventDefault();
  
  const currentUser = this.constants.getSession();
  const currentRole = currentUser?.role?.keyRole;

  if (currentRole === 'HOST') {
    this.router.navigate(['/dashboard']);
    this.closeSidebar();
  }
}
}
